import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Icon,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core';
import useStyles from 'app/cluster-styles/useStyles';
import { IMAGE_TYPE, IMAGE_MODE } from '../../util/enums';
import SectionCard from '../components/SectionCard';
import NodeGroupCard from '../components/NodeGroupCard';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import NodeStackField from '../components/NodeStackField';
import VolumeManager from '../components/VolumeManager';

export default function ComputeNodesSection({ form, setField, provider, disabled, marketplaceComputeIds }) {
    const classes = useStyles();
    const groups = form.nodeGroups || [];
    const imageMode = form.imageMode || IMAGE_MODE.UNIFIED;
    const availableStorageConfigs = form.storageConfiguration?.configurations || [];

    // Volume dropdown state
    const [volumeDropdownOpen, setVolumeDropdownOpen] = useState({});
    const volumeDropdownAnchorEl = useRef({});

    // Node group CRUD
    const addGroup = useCallback(() => {
        setField('nodeGroups', [...groups, {
            name: `compute-${groups.length + 1}`,
            count: 1,
            instance_id: '',
            image_type: IMAGE_TYPE.HEIDI_STACK,
            image_id: '',
            image_name: '',
        }]);
    }, [groups, setField]);

    // Head-node-only clusters are valid, so a group can always be removed - even
    // the last one. The submit path re-injects the required count:0 placeholder.
    const removeGroup = useCallback((index) => {
        setField('nodeGroups', groups.filter((_, i) => i !== index));
    }, [groups, setField]);

    const updateGroup = useCallback((index, field, value) => {
        const updated = [...groups];
        updated[index] = { ...updated[index], [field]: value };
        setField('nodeGroups', updated);
    }, [groups, setField]);

    // Node volume operations
    const addVolumeToNode = useCallback((nodeIndex, storageConfigId) => {
        const config = availableStorageConfigs.find(c => c.id === storageConfigId);
        if (!config) return;
        const headVolume = config.headNodeVolumes?.[0];
        const newVolume = {
            id: `node-${nodeIndex}-volume-${Date.now()}`,
            storageConfigId: config.id,
            mountPath: headVolume?.mountPath || '/data',
            size: headVolume?.overrides?.capacity || null,
            policyId: headVolume?.policy || '',
            policyName: '',
            overrides: {
                capacity: headVolume?.overrides?.capacity || null,
                iops: headVolume?.overrides?.iops || null,
                throughput: headVolume?.overrides?.throughput || null,
            },
            status: 'pending',
        };
        const updated = [...groups];
        updated[nodeIndex] = {
            ...updated[nodeIndex],
            attachedVolumes: [...(updated[nodeIndex].attachedVolumes || []), newVolume],
        };
        setField('nodeGroups', updated);
    }, [groups, availableStorageConfigs, setField]);

    const removeVolumeFromNode = useCallback((nodeIndex, volumeId) => {
        const updated = [...groups];
        updated[nodeIndex] = {
            ...updated[nodeIndex],
            attachedVolumes: (updated[nodeIndex].attachedVolumes || []).filter(v => v.id !== volumeId),
        };
        setField('nodeGroups', updated);
    }, [groups, setField]);

    const handleVolumeUpdate = useCallback((nodeIndex, updatedVolume) => {
        const group = groups[nodeIndex];
        if (!group?.attachedVolumes) return;
        const idx = group.attachedVolumes.findIndex(v => v.id === updatedVolume.id);
        if (idx === -1) return;
        const updatedVolumes = [...group.attachedVolumes];
        updatedVolumes[idx] = updatedVolume;
        const updated = [...groups];
        updated[nodeIndex] = { ...group, attachedVolumes: updatedVolumes };
        setField('nodeGroups', updated);
    }, [groups, setField]);

    return (
        <SectionCard
            icon="device_hub"
            title="Compute Nodes"
            tooltip="The available Instance Sizes and quantity of Instances may vary based on your provider account status."
            action={
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<Icon>add</Icon>}
                    onClick={addGroup}
                    disabled={groups.length >= 10 || disabled}
                    className={classes.grayButton}
                >
                    Add Node Group
                </Button>
            }
        >
            {groups.length === 0 && (
                <Typography variant="body2" color="textSecondary" style={{ padding: '4px 2px' }}>
                    No compute nodes - this is a head-node-only cluster. Click "Add Node Group" to add compute nodes.
                </Typography>
            )}
            {groups.map((group, index) => (
                <NodeGroupCard
                    key={`node-group-${index}`}
                    index={index}
                    label={group.group_node_prefix_label || `Node Group ${index + 1}`}
                    onRemove={() => removeGroup(index)}
                    canRemove
                >
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: imageMode === IMAGE_MODE.MULTI_IMAGE
                            ? '18% 49% 18% 10%'
                            : '55% 25% 15%',
                        gap: '16px',
                        alignItems: 'center',
                        width: '100%',
                    }}>
                        {imageMode === IMAGE_MODE.MULTI_IMAGE && (
                            <NodeStackField
                                provider={provider}
                                value={group.image_name || ''}
                                onChange={(prefix) => updateGroup(index, 'image_name', prefix)}
                                disabled={disabled}
                            />
                        )}

                        <InstanceSizeSelector
                            provider={provider}
                            value={group.instance_id || ''}
                            onChange={(instanceId) => updateGroup(index, 'instance_id', instanceId)}
                            allowedInstanceIds={marketplaceComputeIds}
                            label="Instance Type"
                            disabled={disabled}
                        />

                        <TextField
                            select
                            id={`${provider}_node_image_type_${index}`}
                            name={`node_image_type_${index}`}
                            label="Image Type"
                            variant="outlined"
                            value={group.image_type || IMAGE_TYPE.HEIDI_STACK}
                            onChange={(e) => updateGroup(index, 'image_type', e.target.value)}
                            disabled={disabled}
                        >
                            <MenuItem value={IMAGE_TYPE.HEIDI_STACK}>Heidi Stack</MenuItem>
                            <MenuItem value={IMAGE_TYPE.CUSTOM}>Custom Image</MenuItem>
                        </TextField>

                        <TextField
                            key={`count-${index}-${group.count}-${form?.id}`}
                            label="Count"
                            type="number"
                            value={group.count != null ? group.count : 1}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 0) updateGroup(index, 'count', val);
                            }}
                            variant="outlined"
                            InputProps={{ inputProps: { min: 0, max: 100 } }}
                            disabled={disabled}
                        />
                    </div>

                    {/* Custom Image ID — only when Custom Image selected */}
                    {group.image_type === IMAGE_TYPE.CUSTOM && (
                        <div style={{ marginTop: '16px' }}>
                            <TextField
                                label="Custom Image ID"
                                value={group.image_id || ''}
                                onChange={(e) => updateGroup(index, 'image_id', e.target.value)}
                                variant="outlined"
                                disabled={disabled}
                                fullWidth
                            />
                        </div>
                    )}

                    {/* Volume management — only when storage configs exist */}
                    {availableStorageConfigs.length > 0 && (
                        <Box mt={2}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                    Volumes
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Icon style={{ fontSize: '14px' }}>add</Icon>}
                                    onClick={(e) => {
                                        volumeDropdownAnchorEl.current[index] = e.currentTarget;
                                        setVolumeDropdownOpen(prev => ({ ...prev, [index]: true }));
                                    }}
                                    style={{ fontSize: '11px', padding: '4px 8px', minWidth: 'auto', height: '28px' }}
                                >
                                    Add Volume
                                </Button>
                                <Menu
                                    anchorEl={volumeDropdownAnchorEl.current[index]}
                                    open={Boolean(volumeDropdownOpen[index])}
                                    onClose={() => setVolumeDropdownOpen(prev => ({ ...prev, [index]: false }))}
                                    PaperProps={{ style: { maxHeight: 300, minWidth: 250 } }}
                                >
                                    {availableStorageConfigs.map((config) => (
                                        <MenuItem
                                            key={config.id}
                                            onClick={() => {
                                                addVolumeToNode(index, config.id);
                                                setVolumeDropdownOpen(prev => ({ ...prev, [index]: false }));
                                            }}
                                        >
                                            <Box display="flex" alignItems="center">
                                                <Icon style={{ marginRight: 8, fontSize: '16px' }}>storage</Icon>
                                                <Box>
                                                    <Typography variant="body2" style={{ fontSize: '12px' }}>
                                                        {config.name || config.providerName || 'Unknown Provider'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" style={{ fontSize: '10px' }}>
                                                        {config.providerType ? config.providerType.toUpperCase() : 'UNKNOWN'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                    {availableStorageConfigs.length === 0 && (
                                        <MenuItem disabled>No storage providers available</MenuItem>
                                    )}
                                </Menu>
                            </Box>
                            <VolumeManager
                                volumes={group.attachedVolumes || []}
                                availableStorageConfigs={availableStorageConfigs}
                                onVolumeUpdate={handleVolumeUpdate}
                                onVolumeDelete={removeVolumeFromNode}
                                nodeGroupIndex={index}
                                chipContainerStyle={{
                                    backgroundColor: 'transparent',
                                    padding: '12px',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0',
                                    minHeight: '40px',
                                }}
                            />
                        </Box>
                    )}
                </NodeGroupCard>
            ))}
        </SectionCard>
    );
}
